/**
 * #3131 / #3132 — the SPA's first cookie WRITER.
 *
 * Until now the frontend only ever *read* `document.cookie` (`src/services/csrf.ts`);
 * the session and `csrf_token` cookies are minted server-side by the BFF. The cookie
 * notice (#3131) and the language override (#3132) both need to persist a small,
 * strictly-functional preference client-side, so they share this one primitive
 * rather than each hand-rolling a regex.
 *
 * Deliberately dependency-free — `js-cookie` is not (and need not become) a
 * dependency for two cookies. The read regex mirrors the shape already proven in
 * `src/services/csrf.ts`.
 */

/** Days → seconds, the unit `Max-Age` actually wants. */
const SECONDS_PER_DAY = 24 * 60 * 60;

export interface SetCookieOptions {
  /** Lifetime in days. Written as `Max-Age`. */
  days: number;
  /**
   * Force the `Secure` attribute on or off. Defaults to auto-detection from
   * `window.location.protocol` — see {@link isSecureProtocol} for why this must
   * never be unconditional.
   */
  secure?: boolean;
}

/**
 * `Secure` is correct in production (https) and actively harmful anywhere else:
 * a browser silently DROPS a `Secure` cookie set over http, so the write appears
 * to succeed and the read comes back empty. Two of our own environments are http
 * — vitest's jsdom (`http://localhost:3000`) and the containerised Playwright run
 * (`e2e/helpers/config.ts` resolves `appURL` to `http://nginx` under Docker) — so
 * an unconditional `Secure` would make both the banner dismissal and the language
 * override fail there while passing over local `https://localhost`.
 *
 * Kept as a pure predicate over the protocol string so it is testable without
 * fighting jsdom's non-configurable `window.location`.
 */
export function isSecureProtocol(protocol: string): boolean {
  return protocol === 'https:';
}

/** Escape the characters that would otherwise be regex syntax in a cookie name. */
function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Read a cookie by exact name, or `null` when it is not set.
 *
 * The `(?:^|;\s*)` anchor plus the literal `=` is what makes this an EXACT-name
 * read: neither `hofsync_lang_x` nor `x_hofsync_lang` can satisfy a lookup of
 * `hofsync_lang`. That matters here because both new cookies share the
 * `hofsync_` prefix with each other and with anything added later.
 *
 * Returns `''` (not `null`) for a cookie that is present but empty, so callers
 * can distinguish "acknowledged with an empty value" from "never set".
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escapeForRegExp(name)}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Write a cookie scoped to the whole app.
 *
 * `SameSite=Lax` because neither cookie is needed on a cross-site request, and
 * the value is percent-encoded so a `;` in it cannot forge an attribute.
 */
export function setCookie(name: string, value: string, options: SetCookieOptions): void {
  if (typeof document === 'undefined') return;

  const secure =
    options.secure ?? (typeof window !== 'undefined' && isSecureProtocol(window.location.protocol));

  const attributes = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${Math.floor(options.days * SECONDS_PER_DAY)}`,
  ];
  if (secure) attributes.push('Secure');

  document.cookie = attributes.join('; ');
}

/**
 * Delete a cookie. There is no removal API — the only way is to re-set the name
 * with an already-elapsed lifetime, on the same `Path` it was written to.
 */
export function clearCookie(name: string): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${name}=; Path=/; SameSite=Lax; Max-Age=0`;
}
